import { emitPatientProfileSurfaceEvent } from '@/composition/hosts/patients/patient-profile/usePatientProfileHost';
import { toast } from '@heidi/design-system/toast';
import {
  toAnalyticsFileType,
  usePatientSurfaceAnalytics,
  type PatientProfileSurface,
} from '@heidi/patients';
import {
  UPLOAD_PATIENT_DOCUMENTS,
  useMutateUploadPatientDocuments as useMutateUploadPatientDocumentsCore,
} from '@heidi/patients/data';
import { useIntl } from 'react-intl';

export { UPLOAD_PATIENT_DOCUMENTS };

/**
 * App-side delegate for `@heidi/patients/data` (ARCFE-1054).
 *
 * Everything that reaches the user or the analytics bus stays here; the cache
 * writes (optimistic prepend, per-file progress, settle-time invalidation) all
 * target patients' own `GET_PATIENT_DOCUMENTS` and moved with the body. The
 * ports fire at the exact positions the legacy effects fired — `onUploaded`
 * after the `count === 0` guard inside `onSuccess`, `onUploadError` after the
 * log inside `onError` — so the sequence and the copy are preserved outright.
 *
 * The returned object still spreads the mutation and carries `cancelUpload`,
 * so every call site keeps its exact shape.
 */
export function useMutateUploadPatientDocuments(
  patientProfileId: string,
  // Body-level callers outside the surface-context provider (e.g. the full-page
  // profile) pass their surface explicitly; children rely on context.
  analyticsSurface?: PatientProfileSurface
) {
  const intl = useIntl();
  const { trackDocumentUploaded } = usePatientSurfaceAnalytics(
    emitPatientProfileSurfaceEvent,
    analyticsSurface
  );

  return useMutateUploadPatientDocumentsCore(patientProfileId, {
    onUploaded: (results, files) => {
      const count = results.length;
      // results preserve the input order (Promise.all over files.map), so each
      // result lines up with its source File for size/type.
      results.forEach((doc, i) => {
        const file = files[i];
        if (!doc?.id || !file) return;
        trackDocumentUploaded({
          patientId: patientProfileId,
          documentId: doc.id,
          actor: 'clinician',
          fileSizeBytes: file.size,
          fileType: toAnalyticsFileType(file.type),
        });
      });
      toast({
        title: intl.formatMessage(
          {
            defaultMessage:
              '{count, plural, one {Document attached} other {# documents attached}}',
            id: 'VZrOCO5FWF',
          },
          { count }
        ),
        description: intl.formatMessage(
          {
            defaultMessage:
              '{count, plural, one {The document is now on the patient record.} other {The documents are now on the patient record.}}',
            id: '0NEcrpB+dV',
          },
          { count }
        ),
        tone: 'success',
      });
    },
    onUploadError: () => {
      toast({
        title: intl.formatMessage({
          defaultMessage: 'Upload failed',
          id: 'f00p/7iV78',
        }),
        description: intl.formatMessage({
          defaultMessage: 'One or more documents failed to upload. Please try again.',
          id: 'F4Jy2/shCf',
        }),
        tone: 'destructive',
      });
    },
  });
}
